/** Utility for getting the key type from a key hasher. */
export type KeyHasherKeyType<T extends AnyKeyHasher> = ReturnType<T["unhash"]>;

/** Utility for getting the pattern type from a key hasher. */
export type KeyHasherPatternType<T extends AnyKeyHasher> = Parameters<
  T["matches"]
>[1];

/** Accepts any type of key hasher. */
export type AnyKeyHasher = KeyHasher<unknown, unknown>;

/** An interface for handling key hashing. T is the key type, and P is the pattern type. */
export abstract class KeyHasher<T, P> {
  /** Takes the key type and hashes it into a string. */
  abstract hash(key: T): string;

  /** Takes the hashed key string, and deserializes it back into a key object. */
  abstract unhash(key: string): T;

  /** Takes a pattern and serializes it into a string. */
  abstract hashPattern(pattern: P): string;

  /** Takes a serialized pattern and restores into the original pattern. */
  abstract unhashPattern(pattern: string): P;

  /** Accepts a key object and a pattern, and determines if the pattern matches the key. */
  abstract matches(key: T, pattern: P): boolean;
}

export type StringKeyType = string;
export type StringKeyPattern = string;

/** A basic key hasher for hashing string keys.
 *
 * No actual hashing is used. The string is left as is. Useful as a no-op key hasher when a
 * KeyHasher interface is required. */
export class StringKeyHasher extends KeyHasher<
  StringKeyType,
  StringKeyPattern
> {
  hash(key: string): string {
    return key;
  }

  unhash(key: string): string {
    return key;
  }

  hashPattern(pattern: string): string {
    return pattern;
  }

  unhashPattern(pattern: string): string {
    return pattern;
  }

  /** Accepts a key object and a pattern, and determines if the pattern matches the key.
   *
   * Tests if the key includes the pattern as a substring. */
  matches(key: string, pattern: string): boolean {
    return key.includes(pattern);
  }
}

export type ArrayKeyType = (string | number | boolean | null)[];
export type ArrayKeyPattern = (string | number | boolean | null | undefined)[];

/** A key hasher for hashing array-type keys. */
export class ArrayKeyHasher extends KeyHasher<ArrayKeyType, ArrayKeyPattern> {
  hash(key: ArrayKeyType): string {
    return JSON.stringify(key);
  }

  unhash(key: string): ArrayKeyType {
    return JSON.parse(key);
  }

  hashPattern(pattern: ArrayKeyPattern): string {
    const substituted: unknown[] = [];
    // encode 'undefined' as empty objects: {}
    for (let i = 0; i < pattern.length; i++) {
      substituted.push(pattern[i] === undefined ? {} : pattern[i]);
    }
    // remove encoded 'undefined's from the end of the array:
    // this ensures equal patterns will serialize to the same string
    while (substituted.length) {
      const last = substituted[substituted.length - 1];
      if (!last || typeof last !== "object") break;

      substituted.pop(); // pop empty object
    }
    return JSON.stringify(substituted);
  }

  unhashPattern(pattern: string): ArrayKeyPattern {
    const substituted = JSON.parse(pattern) as unknown[];
    return substituted.map((item) =>
      item && typeof item === "object" ? undefined : item
    ) as ArrayKeyPattern;
  }

  /** Accepts a key object and a pattern, and determines if the pattern matches the key.
   *
   * All non-undefined values in the pattern array must equal the values at those indexes in the
   * key array for the key to match.
   *
   * e.g.
   *
   * ```ts
   * pattern = [1, 2, undefined, 4]
   *
   * // matches
   * [1, 2, 3, 4, 5]
   * [1, 2, 16, 4]
   *
   * // does not match
   * [1, 2]
   * [1, 2, 3]
   * [6, 2, 3, 4]
   * ``` */
  matches(key: ArrayKeyType, pattern: ArrayKeyPattern): boolean {
    for (let i = 0; i < pattern.length; i++) {
      if (pattern[i] === undefined) continue;
      if (key[i] !== pattern[i]) return false;
    }
    return true;
  }
}

export type ObjectKeyType = Record<string, string | number | boolean | null>;
export type ObjectKeyPattern = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface ObjectKeyHasherOptions {
  /** If the property names of each object key should be sorted for this object hasher. Defaults
   * to true.
   *
   * **Setting this to false may improve performance slightly, but is generally not
   * recommended.**
   *
   * If performance is critical, and this must be set to false, take the following precautions:
   * - Verify all object literal expressions include the properties in the same order.
   * - Verify all objects constructed by a series of property assignments always assign the same
   *   property names in the same order.
   * - Verify object literals do not have properties assigned to awaited values, unless the keys
   *   are exclusively constructed in one place. Otherwise transpilers may mangle the order of
   *   the assigned keys. e.g., key order of `{a:1, b:await 2}` may differ from
   *   `{a:await 1, b:2}`.
   *
   * Two safe cases for setting sortKeys to false:
   * - There is only ever one property in the object. Since the property name may vary, this may
   *   suffice for some cases.
   * - A single type of key is only ever constructed in one place. If minifying or transpiling
   *   that code mangles the order, it does so for all constructions of that type of key. */
  sortKeys?: boolean;
}

/** A key hasher for hashing string-table type keys.
 *
 * **Caution:** This hasher may not be used reliably for keys that must be sent over network for
 * remote tables. Since object property ordering techniques may vary in different JavaScript
 * runtimes (and versions thereof), the order of a key's properties may change if serialized on
 * runtime A and then deserialized and reserialized on runtime B. */
export class ObjectKeyHasher extends KeyHasher<
  ObjectKeyType,
  ObjectKeyPattern
> {
  private sortedKeys: boolean;

  constructor({ sortKeys = true }: ObjectKeyHasherOptions = {}) {
    super();
    this.sortedKeys = sortKeys;
  }

  private sortKeys(obj: ObjectKeyType): ObjectKeyType;
  private sortKeys(obj: ObjectKeyPattern): ObjectKeyPattern;
  private sortKeys(obj: ObjectKeyPattern) {
    const sorted = Object.create(null);
    for (const key of Object.keys(obj).sort()) {
      sorted[key] = obj[key];
    }
    return sorted;
  }

  hash(key: ObjectKeyType): string {
    const obj = this.sortedKeys ? this.sortKeys(key) : key;
    return JSON.stringify(obj);
  }

  unhash(key: string): ObjectKeyType {
    return JSON.parse(key);
  }

  hashPattern(pattern: ObjectKeyPattern): string {
    const obj = this.sortedKeys ? this.sortKeys(pattern) : pattern;
    return JSON.stringify(obj);
  }

  unhashPattern(pattern: string): ObjectKeyPattern {
    return JSON.parse(pattern);
  }

  /** Accepts a key object and a pattern, and determines if the pattern matches the key.
   *
   * For each present and non-undefined property of the pattern, the key must have the same
   * value for that property for it to match. */
  matches(key: ObjectKeyType, pattern: ObjectKeyPattern): boolean {
    for (const prop of Object.keys(pattern)) {
      if (pattern[prop] === undefined) continue;
      if (key[prop] !== pattern[prop]) return false;
    }
    return true;
  }
}
